import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { ViewTransactionModalComponent } from './view-transaction-modal.component';
import { environment } from '../../environments/environment';

interface Transaction {
  id: string;
  transaction_id: string;
  type: 'debit' | 'credit';
  retailer_name: string;
  amount: number;
  payment_method: string;
  status: 'success' | 'pending' | 'failed';
  transaction_date: string;
  gateway_payment_id: string | null;
  invoice_no: string | null
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface TransactionSummary {
  total: number;
  successful: number;
  pending: number;
  totalAmount: number;
}

@Component({
  selector: 'app-transactions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SidebarComponent,
    ViewTransactionModalComponent
  ],
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss']
})
export class TransactionsComponent implements OnInit {

  transactions: Transaction[] = [];

  pagination: Pagination = {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
    hasNext: false,
    hasPrev: false
  };

  // Overall summary (not page-scoped)
  summary: TransactionSummary = { total: 0, successful: 0, pending: 0, totalAmount: 0 };

  searchTerm = '';
  statusFilter = '';
  typeFilter = '';
  loading = false;
  invoiceIdFilter = '';
  startDateFilter = '';
  endDateFilter = '';

  showViewModal = false;
  selectedTransaction: Transaction | null = null;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.fetchTransactionsSummary();
    this.route.queryParams.subscribe(params => {
      if (params['invoice_id']) {
        this.invoiceIdFilter = params['invoice_id'];
      }
      this.fetchTransactions();
    });
  }

  fetchTransactionsSummary(): void {
    this.http.get<any>(`${environment.apiBaseUrl}/transaction/summary`).subscribe({
      next: (res) => {
        if (res.statusCode === 200 && res.data) {
          this.summary = res.data;
        }
      },
      error: (err) => {
        console.error('Failed to fetch transaction summary:', err);
      }
    });
  }

  fetchTransactions(page: number = 1): void {
    this.loading = true;

    const params: any = {
      page: page,
      limit: this.pagination.limit
    };

    if (this.searchTerm) params.search = this.searchTerm;
    if (this.statusFilter) params.status = this.statusFilter;
    if (this.typeFilter) params.type = this.typeFilter;
    if (this.invoiceIdFilter) params.invoice_id = this.invoiceIdFilter;
    if (this.startDateFilter) params.start_date = this.startDateFilter;
    if (this.endDateFilter) params.end_date = this.endDateFilter;

    this.http.get<any>(`${environment.apiBaseUrl}/transaction/all`, { params })
      .subscribe({
        next: (response) => {

          const apiData = response?.data?.data || [];
          const pagination = response?.data?.pagination;

          this.transactions = apiData.map((txn: any) => ({
            id: txn.id,
            transaction_id: txn.id,
            type: txn.is_refund ? 'credit' : 'debit',
            retailer_name: txn.retailer_name || txn.fromRetailer?.retailer_name || 'N/A',
            amount: Number(txn.amount),
            payment_method: txn.payment_mode,
            status: txn.status,
            transaction_date: txn.created_at,
            gateway_payment_id: txn.gateway_payment_id,
            invoice_no: txn.invoice_no
          }));

          if (pagination) {
            this.pagination = {
              total: pagination.total,
              page: pagination.page,
              limit: pagination.limit,
              totalPages: pagination.totalPages,
              hasNext: pagination.page < pagination.totalPages,
              hasPrev: pagination.page > 1
            };
          }

          // Scroll to top on page change
          window.scrollTo({ top: 0, behavior: 'smooth' });

          this.loading = false;
        },
        error: (error) => {
          console.error('Error fetching transactions:', error);
          this.loading = false;
        }
      });
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.fetchTransactions(1);
  }

  onStatusFilterChange(): void {
    this.fetchTransactions(1);
  }

  onTypeFilterChange(): void {
    this.fetchTransactions(1);
  }

  onDateFilterChange(): void {
    this.fetchTransactions(1);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.statusFilter = '';
    this.typeFilter = '';
    this.invoiceIdFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
    // Clear query params from URL
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { invoice_id: null },
      queryParamsHandling: 'merge'
    });
    this.fetchTransactions(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pagination.totalPages) return;
    this.fetchTransactions(page);
  }

  viewTransaction(transaction: Transaction): void {
    this.selectedTransaction = transaction;
    this.showViewModal = true;
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.selectedTransaction = null;
  }

  exportToCSV(): void {
    const headers = [
      'Transaction ID',
      'Type',
      'Retailer',
      'Amount',
      'Method',
      'Status',
      'Date'
    ];

    const csvData = this.transactions.map(txn => [
      txn.transaction_id,
      txn.type,
      txn.retailer_name,
      txn.amount,
      txn.payment_method,
      txn.status,
      new Date(txn.transaction_date).toLocaleString()
    ]);

    let csvContent = headers.join(',') + '\n';

    csvData.forEach(row => {
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();

    window.URL.revokeObjectURL(url);
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'success': return 'status-success';
      case 'pending': return 'status-pending';
      case 'failed': return 'status-failed';
      default: return '';
    }
  }

  getTypeClass(type: string): string {
    return type === 'credit' ? 'type-credit' : 'type-debit';
  }

  // Summary Cards — sourced from overall summary, not current page

  getTotalTransactions(): number {
    return this.summary.total;
  }

  getSuccessTransactions(): number {
    return this.summary.successful;
  }

  getPendingTransactions(): number {
    return this.summary.pending;
  }

  getTotalAmount(): number {
    return this.summary.totalAmount;
  }
}
